import { useEffect, useMemo, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import i18n from '../i18n';
import {
  getUserLanguage,
  isSupportedLanguage,
  setUserLanguage,
  type SupportedLanguage,
} from '../services/profileService';

function useDebouncedCallback<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delayMs: number
) {
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  return useMemo(() => {
    return (...args: TArgs) => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => fn(...args), delayMs);
    };
  }, [fn, delayMs]);
}

export default function LanguageSync({ user }: { user: User }) {
  const isHydratingRef = useRef(false);

  const persistToProfile = useDebouncedCallback((language: SupportedLanguage) => {
    setUserLanguage(user.id, language).catch(() => {});
  }, 400);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const profileLanguage = await getUserLanguage(user.id);
      if (cancelled) return;
      if (!profileLanguage) return;
      if (profileLanguage === i18n.language) return;

      isHydratingRef.current = true;
      try {
        await i18n.changeLanguage(profileLanguage);
      } finally {
        isHydratingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user.id]);

  useEffect(() => {
    const handler = (lng: string) => {
      if (isHydratingRef.current) return;
      if (!isSupportedLanguage(lng)) return;
      persistToProfile(lng);
    };

    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, [persistToProfile]);

  return null;
}
